/**
 * B 端经营台注册表（施工规格 §5 全字段）
 * 大门只读本文件，禁止业务页 hardcode 三台 if-else。
 */
import { MALL_BIZ_MODE } from '@/views/merchant/registry/mall-biz-mode'

export type ConsoleId =
  'MALL_SHOP' | 'O2O_ORG' | 'CAPACITY' | 'HOTEL_SHOP' | 'SCENIC_SHOP' | 'RENTAL_SHOP' | 'XUANXUE_SHOP'

export interface ConsoleMenuItem {
  path: string
  title: string
  icon?: string
  /**
   * 侧栏业务域一级标题（可折叠 el-sub-menu）。
   * 连续同 group 的项聚成一级；叶子为二级；无 group 的项一级平铺。
   * 商城勿再套「商城管理」外壳；group 作深蓝一级，叶子进白二级栏。
   */
  group?: string
  /** 子账号访问该菜单所需的精确权限；主账号权限集来自模块种子。 */
  permission?: string
  /**
   * 聚合入口：拥有其中任一权限即可显示（如经营概况、营销管理）。
   * 与 permission 同时配置时，任一命中即显示。
   */
  anyPermissions?: string[]
  /** 仅当会话已开通该模块时展示（如酒店入驻） */
  requireModule?: string
  /** 仅当商城经营模式匹配时展示，如 platform */
  requireBizMode?: string
  /** NOT_APPROVED / NOT_ACTIVE 时仅展示入驻项等 */
  when?: 'NOT_APPROVED' | 'NOT_ACTIVE' | 'ALWAYS'
}

/** 将菜单按 group 字段聚成侧栏分区（保持原顺序） */
export function clusterMenuByGroup(
  menu: ConsoleMenuItem[]
): Array<{ group?: string; items: ConsoleMenuItem[] }> {
  const clusters: Array<{ group?: string; items: ConsoleMenuItem[] }> = []
  for (const item of menu) {
    const g = item.group
    const last = clusters[clusters.length - 1]
    if (last && last.group === g) {
      last.items.push(item)
    } else {
      clusters.push({ group: g, items: [item] })
    }
  }
  return clusters
}

export interface ConsoleDescriptor {
  consoleId: ConsoleId
  title: string
  description: string
  subjectType: string
  routePrefix: string
  homePath: string
  layoutKey: string
  apply: { enabled: boolean; path: string; title: string }
  apiAllowlist: string[]
  menu: ConsoleMenuItem[]
  sortOrder: number
  enabled: boolean
  ownerModule: string
  docsUri?: string
}

export const CONSOLE_IDS = {
  MALL_SHOP: 'MALL_SHOP' as const,
  O2O_ORG: 'O2O_ORG' as const,
  CAPACITY: 'CAPACITY' as const,
  HOTEL_SHOP: 'HOTEL_SHOP' as const,
  SCENIC_SHOP: 'SCENIC_SHOP' as const,
  RENTAL_SHOP: 'RENTAL_SHOP' as const,
  XUANXUE_SHOP: 'XUANXUE_SHOP' as const
}

const MALL: ConsoleDescriptor = {
  consoleId: 'MALL_SHOP',
  title: '商城经营台',
  description: '发品、实物订单、店铺与结算',
  subjectType: 'shop',
  routePrefix: '/store',
  homePath: '/store/dashboard',
  layoutKey: 'MallConsoleLayout',
  // B 大门不做入驻（见 上门服务文档/B端大门-只登录不入驻-2026-07-24.md）
  apply: {
    enabled: false,
    path: '',
    title: ''
  },
  apiAllowlist: [
    '/merchant-api/merchant/shop/**',
    '/merchant-api/merchant/shop-category/**',
    '/merchant-api/product/**',
    '/merchant-api/order/**',
    '/merchant-api/trade/**',
    '/merchant-api/promotion/**',
    '/merchant-api/live/**',
    '/merchant-api/groupbuy/**',
    '/merchant-api/merchant/finance/**',
    '/merchant-api/merchant/withdraw/**',
    '/merchant-api/merchant/sub-account/**',
    '/merchant-api/agent/belonging/**',
    '/merchant-api/system/area/**'
  ],
  // 侧栏按业务域 group 分区（不套「商城管理」折叠壳）；顺序即展示顺序
  menu: [
    {
      path: '/store/dashboard',
      title: '经营概况',
      icon: 'ep:data-board',
      // 概况页聚合多接口；任一业务查询权即可进入（页面内无权限接口会静默降级）
      anyPermissions: [
        'merchant:mall:dashboard:query',
        'merchant:order:query',
        'shop:product:query',
        'shop:info:query',
        'shop:finance:wallet'
      ]
    },
    {
      path: '/store/product',
      title: '商品管理',
      icon: 'ep:goods',
      group: '商品管理',
      permission: 'shop:product:query'
    },
    {
      path: '/store/product/comment',
      title: '商品评价',
      icon: 'ep:chat-line-square',
      group: '商品管理',
      permission: 'shop:product:comment:query'
    },
    {
      path: '/store/product/pool',
      title: '供应选品',
      icon: 'ep:box',
      group: '商品管理',
      permission: 'product:spu:query'
    },
    {
      path: '/store/order',
      title: '订单管理',
      icon: 'ep:document',
      group: '订单履约',
      permission: 'merchant:order:query'
    },
    {
      path: '/store/order/verify',
      title: '自提核销',
      icon: 'ep:full-screen',
      group: '订单履约',
      permission: 'merchant:order:pick-up'
    },
    {
      path: '/store/shop/pick-up-store',
      title: '自提网点',
      icon: 'ep:location',
      group: '订单履约',
      // 自提点接口复用订单查询权限
      permission: 'merchant:order:query'
    },
    {
      path: '/store/shop/express-template',
      title: '运费模版',
      icon: 'ep:delivery',
      group: '订单履约',
      permission: 'trade:delivery:express-template:query'
    },
    {
      path: '/store/finance',
      title: '财务管理',
      icon: 'ep:wallet',
      group: '财务管理',
      anyPermissions: ['shop:finance:wallet', 'shop:finance:bill', 'shop:finance:withdraw']
    },
    {
      path: '/store/finance/invoice',
      title: '发票管理',
      icon: 'ep:document',
      group: '财务管理',
      permission: 'merchant:invoice:query',
      requireBizMode: MALL_BIZ_MODE.PLATFORM
    },
    {
      path: '/store/finance/supplier-settle-preview',
      title: '供方预估',
      icon: 'ep:data-line',
      group: '财务管理',
      permission: 'shop:finance:bill'
    },
    {
      path: '/store/finance/supplier-settle',
      title: '外发结算',
      icon: 'ep:tickets',
      group: '财务管理',
      permission: 'shop:finance:bill'
    },
    {
      path: '/store/promotion',
      title: '营销管理',
      icon: 'ep:present',
      group: '营销管理',
      anyPermissions: [
        'shop:promotion:coupon-template:query',
        'shop:promotion:coupon:query',
        'shop:promotion:discount-activity:query',
        'shop:promotion:seckill-activity:query',
        'shop:promotion:reward-activity:query',
        'shop:promotion:combination-activity:query',
        'shop:promotion:bargain-activity:query'
      ]
    },
    {
      path: '/store/shop',
      title: '店铺管理',
      icon: 'ep:shop',
      group: '店铺管理',
      permission: 'shop:info:query'
    },
    {
      path: '/store/shop/category',
      title: '经营类目',
      icon: 'ep:files',
      group: '店铺管理',
      permission: 'shop:category:list'
    },
    {
      path: '/store/kefu',
      title: '本店客服',
      icon: 'ep:chat-dot-round',
      group: '客服服务',
      permission: 'shop:promotion:kefu:query'
    },
    {
      path: '/store/member/sub-account',
      title: '员工管理',
      icon: 'ep:user-filled',
      permission: 'shop:member:sub-account:query'
    },
    // 社区团购（主体开通 MALL_GROUPBUY 后可见）
    {
      path: '/store/groupbuy/activity',
      title: '团购活动',
      icon: 'ep:shopping-bag',
      group: '社区团购',
      permission: 'merchant:groupbuy:activity:query',
      requireModule: 'MALL_GROUPBUY'
    },
    {
      path: '/store/groupbuy/pickup-point',
      title: '团购自提',
      icon: 'ep:place',
      group: '社区团购',
      permission: 'merchant:groupbuy:pickup-point:query',
      requireModule: 'MALL_GROUPBUY'
    },
    {
      path: '/store/groupbuy/leader',
      title: '团长审核',
      icon: 'ep:user',
      group: '社区团购',
      permission: 'merchant:groupbuy:leader:query',
      requireModule: 'MALL_GROUPBUY'
    },
    {
      path: '/store/groupbuy/driver',
      title: '司机审核',
      icon: 'ep:van',
      group: '社区团购',
      permission: 'merchant:groupbuy:driver:query',
      requireModule: 'MALL_GROUPBUY'
    },
    {
      path: '/store/groupbuy/batch',
      title: '批次台账',
      icon: 'ep:box',
      group: '社区团购',
      permission: 'merchant:groupbuy:activity:query',
      requireModule: 'MALL_GROUPBUY'
    },
    {
      path: '/store/groupbuy/report',
      title: '团购对账',
      icon: 'ep:data-analysis',
      group: '社区团购',
      permission: 'merchant:groupbuy:activity:query',
      requireModule: 'MALL_GROUPBUY'
    }
  ],
  sortOrder: 30,
  enabled: true,
  ownerModule: 'yudao-module-merchant + mall',
  docsUri: '上门服务文档/商城/'
}

/**
 * 直播带货菜单（从商城侧栏拆出，作为「直播管理」一级大类）。
 * 仍依赖 MALL 模块 entitlement；权限码不变。
 */
export const LIVE_MENU: ConsoleMenuItem[] = [
  {
    path: '/store/live/product',
    title: '直播商品',
    icon: 'ep:goods',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query']
  },
  {
    path: '/store/live/orders',
    title: '直播订单',
    icon: 'ep:shopping-cart',
    group: '直播管理',
    permission: 'merchant:order:query'
  },
  {
    path: '/store/live/anchor',
    title: '直播主播',
    icon: 'ep:video-camera',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query', 'merchant:order:query']
  },
  {
    path: '/store/live/commission',
    title: '直播佣金',
    icon: 'ep:coin',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query', 'merchant:order:query']
  },
  {
    path: '/store/live/agreement',
    title: '直播协议',
    icon: 'ep:document-checked',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query', 'merchant:order:query']
  },
  {
    path: '/store/live/settle',
    title: '直播结算',
    icon: 'ep:money',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query', 'merchant:order:query']
  },
  {
    path: '/store/live/activity',
    title: '直播活动',
    icon: 'ep:present',
    group: '直播管理',
    anyPermissions: ['shop:live:query', 'shop:product:query', 'merchant:order:query']
  }
]

const O2O: ConsoleDescriptor = {
  consoleId: 'O2O_ORG',
  title: '上门经营台',
  description: '机构收单、内派、门店预约、机构财务',
  subjectType: 'institution',
  routePrefix: '/store/o2o',
  homePath: '/store/o2o/order',
  layoutKey: 'O2oConsoleLayout',
  // 入驻改 A 端运营审核；B 仅登录进台
  apply: {
    enabled: false,
    path: '',
    title: ''
  },
  apiAllowlist: ['/merchant-api/o2o/**'],
  menu: [
    {
      path: '/store/o2o/institution/profile',
      title: '机构信息',
      icon: 'ep:office-building',
      permission: 'merchant:o2o:institution:query'
    },
    {
      path: '/store/o2o/order',
      title: '服务订单',
      icon: 'ep:list',
      permission: 'merchant:o2o:order:query'
    },
    {
      path: '/store/o2o/provider',
      title: '员工管理',
      icon: 'ep:user',
      permission: 'merchant:o2o:provider:query'
    },
    {
      path: '/store/o2o/store',
      title: '门店管理',
      icon: 'ep:location',
      permission: 'merchant:o2o:store:query'
    },
    {
      path: '/store/o2o/reservation',
      title: '预约管理',
      icon: 'ep:calendar',
      permission: 'merchant:o2o:reservation:query'
    },
    {
      path: '/store/o2o/review',
      title: '评价管理',
      icon: 'ep:chat-dot-round',
      permission: 'merchant:o2o:review:query'
    },
    {
      path: '/store/o2o/pricing/apply',
      title: '服务定价',
      icon: 'ep:money',
      permission: 'merchant:o2o:pricing:query'
    },
    {
      path: '/store/o2o/dispute',
      title: '纠纷记录',
      icon: 'ep:warning',
      permission: 'merchant:o2o:dispute:query'
    },
    {
      path: '/store/o2o/finance',
      title: '机构财务',
      icon: 'ep:wallet',
      permission: 'merchant:o2o:finance:query'
    },
    {
      path: '/store/o2o/dashboard',
      title: '数据看板',
      icon: 'ep:data-analysis',
      permission: 'merchant:o2o:dashboard:query'
    }
  ],
  sortOrder: 10,
  enabled: true,
  ownerModule: 'yudao-module-o2o',
  docsUri: '上门服务文档/B端_文档业务闭环.md'
}

const CAPACITY: ConsoleDescriptor = {
  consoleId: 'CAPACITY',
  title: '运力经营台',
  description: '运力开站、邀骑手、运单、分润确认',
  subjectType: 'capacity_org',
  routePrefix: '/store/capacity',
  homePath: '/store/capacity/dashboard',
  layoutKey: 'CapacityConsoleLayout',
  // 入驻改 A 端运力机构平台；B 仅登录进台看机构信息
  apply: {
    enabled: false,
    path: '',
    title: ''
  },
  apiAllowlist: [
    '/merchant-api/capacity/org-console/**',
    '/merchant-api/capacity/dispatch/**',
    '/merchant-api/capacity/wallet/**'
  ],
  menu: [
    {
      path: '/store/capacity/profile',
      title: '机构信息',
      icon: 'ep:office-building',
      permission: 'merchant:capacity:profile:query'
    },
    {
      path: '/store/capacity/dashboard',
      title: '运力概览',
      icon: 'ep:data-board',
      // 概览与机构信息同源；种子角色亦含 profile
      anyPermissions: ['merchant:capacity:profile:query', 'merchant:capacity:dispatch:query']
    },
    {
      path: '/store/capacity/invite',
      title: '邀请骑手',
      icon: 'ep:ticket',
      permission: 'merchant:capacity:invite:query'
    },
    {
      path: '/store/capacity/dispatch',
      title: '运单监控',
      icon: 'ep:monitor',
      permission: 'merchant:capacity:dispatch:query'
    },
    {
      path: '/store/capacity/settle',
      title: '分润对账',
      icon: 'ep:wallet',
      permission: 'merchant:capacity:settle:query'
    }
  ],
  sortOrder: 20,
  enabled: true,
  ownerModule: 'yudao-module-capacity',
  docsUri: '骑手端/骑手端/一期业务闭环规划-L1.md'
}

const HOTEL: ConsoleDescriptor = {
  consoleId: 'HOTEL_SHOP',
  title: '酒店经营台',
  description: '房态、量价、订单与客房服务',
  subjectType: 'shop',
  routePrefix: '/store/hotel',
  homePath: '/store/hotel/dashboard',
  layoutKey: 'HotelConsoleLayout',
  apply: { enabled: false, path: '', title: '' },
  apiAllowlist: ['/merchant-api/hotel/**'],
  menu: [
    {
      path: '/store/hotel/dashboard',
      title: '经营看板',
      icon: 'ep:data-board',
      permission: 'merchant:hotel:dashboard:query'
    },
    {
      path: '/store/hotel/room-types',
      title: '房型与价型',
      icon: 'ep:collection',
      permission: 'merchant:hotel:room:query'
    },
    {
      path: '/store/hotel/rooms',
      title: '实体房间',
      icon: 'ep:house',
      permission: 'merchant:hotel:room:query'
    },
    {
      path: '/store/hotel/inventory',
      title: '日历量价',
      icon: 'ep:calendar',
      permission: 'merchant:hotel:price-inventory:query'
    },
    {
      path: '/store/hotel/orders',
      title: '预订订单',
      icon: 'ep:tickets',
      permission: 'merchant:hotel:order:query'
    },
    {
      path: '/store/hotel/check-in',
      title: '入住办理',
      icon: 'ep:user-filled',
      permission: 'merchant:hotel:order:query'
    },
    {
      path: '/store/hotel/services',
      title: '客房服务',
      icon: 'ep:service',
      permission: 'merchant:hotel:service:query'
    },
    {
      path: '/store/hotel/finance',
      title: '酒店财务',
      icon: 'ep:wallet',
      permission: 'merchant:hotel:finance:query'
    },
    {
      path: '/store/hotel/settings',
      title: '运营策略',
      icon: 'ep:setting',
      permission: 'merchant:hotel:profile:query'
    },
    {
      path: '/store/hotel/content',
      title: '酒店介绍与装修',
      icon: 'ep:edit',
      permission: 'merchant:hotel:content:query'
    },
    {
      path: '/store/hotel/channel',
      title: '公众号与小程序',
      icon: 'ep:connection',
      permission: 'merchant:hotel:channel:query'
    },
    {
      path: '/store/hotel/members',
      title: '酒店会员',
      icon: 'ep:user',
      permission: 'merchant:hotel:member:query'
    },
    {
      path: '/store/hotel/rent-policy',
      title: '租期与价格规则',
      icon: 'ep:calendar',
      permission: 'merchant:hotel:rent-policy:query'
    },
    {
      path: '/store/hotel/coupons',
      title: '优惠券发放',
      icon: 'ep:ticket',
      permission: 'merchant:hotel:coupon:query'
    }
  ],
  sortOrder: 40,
  enabled: true,
  ownerModule: 'yudao-module-hotel'
}

/** 景区经营台：从商城拆出，独立开通 SCENIC 后展示 */
const SCENIC: ConsoleDescriptor = {
  consoleId: 'SCENIC_SHOP',
  title: '景区经营台',
  description: '景区景点与门票订单',
  subjectType: 'shop',
  routePrefix: '/store/scenic',
  homePath: '/store/scenic/spot',
  layoutKey: 'ScenicConsoleLayout',
  apply: { enabled: false, path: '', title: '' },
  apiAllowlist: ['/merchant-api/scenic/merchant/**'],
  menu: [
    {
      path: '/store/scenic/spot',
      title: '景区管理',
      icon: 'ep:place',
      permission: 'scenic:merchant:spot:query'
    },
    {
      path: '/store/scenic/order',
      title: '景区订单',
      icon: 'ep:tickets',
      permission: 'scenic:merchant:order:query'
    }
  ],
  sortOrder: 35,
  enabled: true,
  ownerModule: 'yudao-module-scenicarea'
}

/** 租赁经营台：商家自助上架租赁商品 */
const RENTAL: ConsoleDescriptor = {
  consoleId: 'RENTAL_SHOP',
  title: '租赁经营台',
  description: '租赁商品上架与本店经营',
  subjectType: 'shop',
  routePrefix: '/store/rental',
  homePath: '/store/rental/product',
  layoutKey: 'RentalConsoleLayout',
  apply: { enabled: false, path: '', title: '' },
  apiAllowlist: ['/merchant-api/rental/**'],
  menu: [
    {
      path: '/store/rental/product',
      title: '商品管理',
      icon: 'ep:goods',
      permission: 'rental:product:query'
    }
  ],
  sortOrder: 45,
  enabled: true,
  ownerModule: 'yudao-module-rental'
}

/** 玄学机构台：机构入驻审核通过后自动开通（MVP 空壳，功能页随版本逐步开放） */
const XUANXUE: ConsoleDescriptor = {
  consoleId: 'XUANXUE_SHOP',
  title: '玄学机构台',
  description: '玄学机构资料与后续经营功能',
  subjectType: 'shop',
  routePrefix: '/store/xuanxue',
  homePath: '/store/xuanxue/institution',
  layoutKey: 'XuanxueConsoleLayout',
  apply: { enabled: false, path: '', title: '' },
  apiAllowlist: ['/merchant-api/xuanxue/**'],
  menu: [
    {
      path: '/store/xuanxue/institution',
      title: '机构资料',
      icon: 'ep:school',
      permission: 'xuanxue:institution:query'
    },
    {
      path: '/store/xuanxue/affiliation',
      title: '旗下师傅',
      icon: 'ep:avatar',
      permission: 'xuanxue:institution:query'
    },
    {
      path: '/store/xuanxue/premium',
      title: '高端服务',
      icon: 'ep:medal',
      permission: 'xuanxue:institution:query'
    }
  ],
  sortOrder: 50,
  enabled: true,
  ownerModule: 'yudao-module-xuanxue'
}

const REGISTRY: ConsoleDescriptor[] = [O2O, CAPACITY, MALL, SCENIC, HOTEL, RENTAL, XUANXUE]

export function listEnabledConsoles(): ConsoleDescriptor[] {
  return REGISTRY.filter((c) => c.enabled).sort((a, b) => a.sortOrder - b.sortOrder)
}

export function listApplyCatalog(): ConsoleDescriptor[] {
  return listEnabledConsoles().filter((c) => c.apply.enabled)
}

export function getConsole(consoleId: string | null | undefined): ConsoleDescriptor | undefined {
  if (!consoleId) return undefined
  return REGISTRY.find((c) => c.consoleId === consoleId)
}

/** Flag：false 时 Layout 恢复三块并存（仅应急） */
export function isPortalGuardEnabled(): boolean {
  const v = localStorage.getItem('b_portal_guard')
  if (v === null || v === '') return true
  return v !== 'false' && v !== '0'
}

export function setPortalGuardEnabled(on: boolean) {
  localStorage.setItem('b_portal_guard', on ? 'true' : 'false')
}
